import * as React from 'react';
import { Button, Card } from 'reactstrap';
import { BtnSelect, IBtnSelectOption } from '../../ui/BtnSelect';
import { HttpRequest } from '../../../util/http';
import { ContentCardHeader } from '../../ui/ContentCardHeader';
import { DataGrid, DataGridSortOrder } from '../../ui/DataGrid';
import { FontAwesomeIcon } from '../../ui/FontAwesomeIcon';
import { GetAllSegments } from '../../util';
import { ISegment } from '../interfaces';
import { SegmentEditor } from '../SegmentEditor';
import { getParentSegments } from '../utils';

interface ISegmentsDataGridProps {
  reloadSegmentsFn?: (loadAvidsFn: () => Promise<void>) => void
}

interface ISegmentsDataGridState {
  segments: ISegment[]
  selectedSegmentIds: number[]
  sortOrder: DataGridSortOrder
  sortBy: keyof ISegment
  disableActions: boolean
  segmentInEditMode?: ISegment
}

export class SegmentsDataGrid extends React.Component<ISegmentsDataGridProps, ISegmentsDataGridState> {
  state: ISegmentsDataGridState = {
    segments: [],
    selectedSegmentIds: [],
    sortOrder: 'desc',
    sortBy: 'id',
    disableActions: false,
  }

  componentWillMount() {
    this.loadSegments()

    if (this.props.reloadSegmentsFn) {
      this.props.reloadSegmentsFn(this.loadSegments.bind(this))
    }
  }

  deleteSegment(segment: ISegment, reloadAvids = true, confirmDelete = true) {
    const { disableActions } = this.state
    const { id } = segment
    const data = { id }
    const method = 'POST'
    const endpoint = 'scripts/segments/delete'

    if (disableActions) { return }

    if (confirmDelete) {
      const confirmDeletion = window.confirm(`Are you sure you want to delete "${id}" segment?`)

      if (!confirmDeletion) { return }
    }

    // Remove from dataset
    const { segments, selectedSegmentIds } = this.state
    const deletingSegmentIndex = segments.findIndex((s) => s.id === id)
    const selectedSegmentIndex = selectedSegmentIds.indexOf(id)

    segments.splice(deletingSegmentIndex, 1)

    if (selectedSegmentIndex >= 0) {
      selectedSegmentIds.splice(selectedSegmentIndex, 1)
    }

    this.setState({ segments, selectedSegmentIds, disableActions: true })

    const deletePromise = HttpRequest.fetch(endpoint, { data, method })

    if (reloadAvids) {
      deletePromise.finally(() => this.loadSegments())
    }

    return deletePromise
  }

  bulkDelete() {
    const { selectedSegmentIds, segments } = this.state
    const selectedAvidsLength = selectedSegmentIds.length
    const confirmDelete = confirm(`Are you sure you want to delete ${selectedAvidsLength} Segments?`)

    if (!confirmDelete) { return; }

    this.setState({ disableActions: true })

    const selectedAvidContainers = segments.filter((segment) => selectedSegmentIds.indexOf(segment.id) >= 0)
    const deletePromises = selectedAvidContainers.map((segment) => this.deleteSegment(segment, false, false))

    Promise.all(deletePromises).finally(() => this.loadSegments())
  }

  loadSegments() {
    this.setState({ disableActions: true })

    return GetAllSegments()
      .then((segments) => this.setState({ segments }))
      .finally(() => this.setState({ disableActions: false }))
  }

  toggleSelected(segment: ISegment) {
    const { selectedSegmentIds } = this.state
    const selectedIndex = selectedSegmentIds.indexOf(segment.id)

    if (selectedIndex >= 0) {
      selectedSegmentIds.splice(selectedIndex, 1)
    } else {
      selectedSegmentIds.push(segment.id)
    }

    this.setState({ selectedSegmentIds })
  }

  isSelected(segment: ISegment) {
    const { selectedSegmentIds } = this.state
    const selectedIndex = selectedSegmentIds.indexOf(segment.id)

    return selectedIndex >= 0
  }

  segmentClicked(segmentInEditMode: ISegment) {
    const currentShownSegment = this.state.segmentInEditMode
    const shouldToggle = currentShownSegment && segmentInEditMode.id === currentShownSegment.id
    segmentInEditMode = shouldToggle ? undefined : segmentInEditMode

    this.setState({ segmentInEditMode })
  }

  removeShownSegment(reloadAvids = false) {
    if (reloadAvids) {
      return this.loadSegments().then(() => this.setState({ segmentInEditMode: undefined }))
    } else {
      this.setState({ segmentInEditMode: undefined })
    }
  }

  async updateParentIds(parentId: string) {
    const parentIdNum = Number(parentId)
    const { selectedSegmentIds, segments } = this.state
    const parentSegments = getParentSegments(segments)
    const selectedSegments = segments.filter((s) => selectedSegmentIds.indexOf(s.id) >= 0)
    const bulkParentSegment = parentSegments.find((ps) => ps.id === parentIdNum)

    if (!bulkParentSegment) { return }

    this.setState({ disableActions: true })

    for (const selectedSegment of selectedSegments) {
      const method = 'POST'
      const data = { ...selectedSegment }

      data.segmentValueAvid = bulkParentSegment.segmentValueAvid
      data.segmentName = bulkParentSegment.segmentName
      data.segmentAvid = bulkParentSegment.segmentAvid

      //must do fetches in sequence otherwise DB lock occurs
      await HttpRequest.fetch('/scripts/segments/save', { data, method })
    }

    await this.loadSegments()

    this.setState({ selectedSegmentIds: [], disableActions: false })
  }

  renderBulkCheck(segment: ISegment) {
    const { disableActions } = this.state
    const isSelected = this.isSelected(segment)
    const isNotSelected = !isSelected
    const icon = isSelected ? 'check-square' : 'square'
    const className = (isNotSelected || disableActions) ? 'text-dark' : ''
    const iconElement = (
      <FontAwesomeIcon className={className}
        verticalAlign="middle"
        icon={icon}
        regular={isNotSelected && !disableActions} />
    )

    if (disableActions) {
      return (<span>{iconElement}</span>)
    }

    return (
      <a href="javascript:void(0)" onClick={() => this.toggleSelected(segment)}>
        {iconElement}
      </a>
    )
  }

  render() {
    const { selectedSegmentIds, segmentInEditMode, disableActions, segments } = this.state
    const selectedSegmentsLength = selectedSegmentIds.length
    const hasSelectedSegments = selectedSegmentsLength > 0
    const shouldDisableBulkActions = !hasSelectedSegments || disableActions
    const parentSegments = getParentSegments(segments)
    const parentSegmentOptions: IBtnSelectOption[] = parentSegments.map(({ segmentName: label, id: value }) => ({ value: String(value), label }))

    return (
      <Card>
        <ContentCardHeader sticky borderBottom loading={disableActions}
          header={
            <>
              <span>Segments</span>
              {hasSelectedSegments &&
                <small>:&nbsp;{selectedSegmentsLength.toLocaleString()}&nbsp;selected</small>
              }
            </>
          }
          headerActions={
            <section className="mt-2">
              <BtnSelect header="Bulk Update Parent Segment"
                color="primary"
                clearLabel="Cancel"
                options={parentSegmentOptions}
                disabled={shouldDisableBulkActions}
                onChange={(parentId) => this.updateParentIds(parentId)}
                clearable
                persistLabel
                disableBoldHeader />
              <Button className="ml-2" color="primary"
                disabled={shouldDisableBulkActions}
                onClick={() => this.bulkDelete()}>
                Bulk Delete
                </Button>
              <Button className="ml-2" color="primary"
                disabled={shouldDisableBulkActions}
                onClick={() => this.setState({ selectedSegmentIds: [] })}>
                Clear Selection
                </Button>
            </section>
          } />
        <DataGrid<ISegment>
          records={this.state.segments}
          sortBy={this.state.sortBy}
          sortOrder={this.state.sortOrder}
          onSortChange={(sortBy, sortOrder) => this.setState({ sortBy, sortOrder })}
          showSubComponent={(segment) => segmentInEditMode && segment.id === segmentInEditMode.id}
          subComponent={() =>
            <SegmentEditor
              editMode={segmentInEditMode}
              onSuccess={() => this.removeShownSegment(true)}
              onCancel={() => this.removeShownSegment()} />
          }
          columns={[
            { header: 'Bulk', align: 'center', sortable: false, render: (segment) => this.renderBulkCheck(segment) },
            {
              header: 'ID', property: 'id', render: (segment) => (
                <>
                  {disableActions ?
                    <span>{segment.id}</span>
                    :
                    <a href="javascript:void(0)"
                      onClick={() => this.segmentClicked(segment)}>
                      <FontAwesomeIcon icon="edit" verticalAlign="initial" size="md" />
                      <span>&nbsp;{segment.id}</span>
                    </a>
                  }
                </>
              )
            },
            { header: 'Parent Label', property: 'segmentName' },
            { header: 'Segment Label', property: 'segmentValueName' },
            { header: 'File Column', property: 'fileColumnName' },
            { header: 'File Value', property: 'fileValue' },
            { header: 'Sort Order', property: 'sortOrder' },
            {
              header: 'Delete', align: 'center', sortable: false, render: (avid) => (
                <a href="javascript:void(0)"
                  onClick={() => this.deleteSegment(avid)}>
                  <FontAwesomeIcon icon="trash-alt" size="md" verticalAlign="middle" muted={disableActions} />
                </a>
              )
            },
          ]} />
      </Card>
    )
  }
}
